import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, History, Package, X } from 'lucide-react';
import { erpAPI } from '../../services/api';
import Toast from '../../components/admin/Toast';
import { rows, errorText, money, date, variantDisplay, Loading, Form } from '../../utils/erpHelpers';
import '../../components/admin/AdminFormStyles.css';
import './ERPManagement.css';

type Tab = 'adjust' | 'history';

const tabMeta: Record<Tab, { label: string; icon: React.ReactNode; color: string }> = {
  adjust: { label: 'Adjust Stock', icon: <AlertTriangle size={16} />, color: '#f59e0b' },
  history: { label: 'Adjustment History', icon: <History size={16} />, color: '#0ea5e9' },
};

const InventoryStockAdjustment: React.FC = () => {
  const [tab, setTab] = useState<Tab>('adjust');
  const [locations, setLocations] = useState<any[]>([]);
  const [inventoryStock, setInventoryStock] = useState<any[]>([]);
  const [adjustments, setAdjustments] = useState<any[]>([]);
  const [reasons, setReasons] = useState<any[]>([]);
  const [settings, setSettings] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'warning' | 'info'; text: string } | null>(null);
  const closeToast = useCallback(() => setMessage(null), []);
  const [locationFilter, setLocationFilter] = useState(() => sessionStorage.getItem('erp-location-filter') || '');
  const [form, setForm] = useState<Record<string, any>>({
    product: '', variant: '', location: '', color: '', size: '', barcode: '',
    quantity: 0, unit_cost: 0, notes: '', adjustment_reason: '',
  });
  const [adjustingStock, setAdjustingStock] = useState<any>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = {};
      if (locationFilter) params.location = locationFilter;
      const adjParams: any = { ...(Object.keys(params).length ? params : {}) };
      const [locRes, invRes, adjRes, reasonRes] = await Promise.all([
        erpAPI.list('locations'),
        erpAPI.list('inventory', Object.keys(params).length ? params : undefined),
        erpAPI.list('stock-adjustments', Object.keys(adjParams).length ? adjParams : undefined),
        erpAPI.list('stock-adjustment-reasons', { is_active: true }),
      ]);
      setLocations(rows(locRes.data));
      setInventoryStock(rows(invRes.data));
      setAdjustments(rows(adjRes.data));
      setReasons(rows(reasonRes.data));
    } catch (error) { setMessage({ type: 'error', text: errorText(error) }); }
    finally { setLoading(false); }
  }, [locationFilter]);

  const loadSettings = useCallback(async () => {
    try {
      const res = await erpAPI.list('inventory-settings', {});
      const data = res.data?.results || res.data;
      setSettings(Array.isArray(data) ? data[0] : data);
    } catch { /* settings are cosmetic for preview */ }
  }, []);

  useEffect(() => { load(); loadSettings(); }, [load, loadSettings]);
  useEffect(() => {
    const change = (event: Event) => {
      const location = String((event as CustomEvent).detail?.location || '');
      sessionStorage.setItem('erp-location-filter', location);
      setLocationFilter(location);
    };
    window.addEventListener('erp-location-change', change);
    return () => window.removeEventListener('erp-location-change', change);
  }, []);
  useEffect(() => {
    const fn = () => load(); window.addEventListener('admin-refresh', fn);
    return () => window.removeEventListener('admin-refresh', fn);
  }, [load]);

  const value = (key: string) => form[key] ?? '';

  const openAdjustment = (stock: any) => {
    setForm({
      product: stock.product || '',
      variant: stock.product_variant || '',
      location: stock.location || '',
      color: stock.variant_color || '',
      size: stock.variant_size || '',
      barcode: stock.barcode || '',
      quantity: stock.quantity || 0,
      unit_cost: stock.average_cost || 0,
      notes: '',
      adjustment_reason: '',
    });
    setAdjustingStock(stock);
  };

  const closeAdjustment = () => {
    if (saving) return;
    setAdjustingStock(null);
  };

  const selectedReason = reasons.find(r => r.id === Number(value('adjustment_reason')));
  const currentQty = Number(adjustingStock?.quantity || 0);
  const requestedQty = Number(value('quantity') || 0);
  const delta = requestedQty - currentQty;
  const direction: 'LOSS' | 'GAIN' | null = selectedReason
    ? (selectedReason.type === 'BOTH'
        ? (delta >= 0 ? 'GAIN' : 'LOSS')
        : selectedReason.type)
    : null;
  const isGain = direction === 'GAIN';
  const unitValue = isGain ? (Number(value('unit_cost')) || 0) : Number(adjustingStock?.average_cost || 0);
  const totalValue = Math.abs(delta) * unitValue;

  const canSave = Boolean(
    adjustingStock &&
    selectedReason &&
    Math.abs(delta) > 0 &&
    (!selectedReason.requires_note || String(value('notes') || '').trim()) &&
    (!isGain || Number(value('unit_cost')) > 0),
  );

  const saveAdjustment = async () => {
    if (!adjustingStock || !canSave) return;
    setSaving(true);
    try {
      const payload: any = {
        location: Number(value('location')),
        product_variant: Number(value('variant')),
        reason: Number(value('adjustment_reason')),
        quantity: requestedQty,
        notes: value('notes') || '',
      };
      if (isGain) payload.unit_cost = Number(value('unit_cost'));
      await erpAPI.create('stock-adjustments', payload);
      setMessage({ type: 'success', text: `Stock adjusted — ${selectedReason?.name}.` });
      setAdjustingStock(null);
      await load();
    } catch (error) {
      setMessage({ type: 'error', text: errorText(error) });
    }
    finally { setSaving(false); }
  };

  const showLocationColumn = !locationFilter;
  const account = (key: string) => settings?.[key];
  const assetAcct = account('inventory_asset_account_code');
  const assetName = account('inventory_asset_account_name');
  const expenseAcct = account('adjustment_expense_account_code');
  const expenseName = account('adjustment_expense_account_name');
  const gainAcct = account('adjustment_gain_account_code');
  const gainName = account('adjustment_gain_account_name');

  const inputStyle: React.CSSProperties = {
    width: '100%', minHeight: 40, padding: '8px 12px',
    border: '1px solid #e2e8f0', borderRadius: 8,
    fontSize: 13, color: '#1e293b', background: '#fff',
    boxSizing: 'border-box', outline: 'none',
    transition: 'border-color 0.15s',
  };

  const fieldLabel: React.CSSProperties = {
    display: 'block', fontSize: 11, fontWeight: 700, color: '#64748b',
    textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6,
  };

  if (loading && !inventoryStock.length) return <Loading />;

  return (
    <div className="erp-page">
      <Toast show={Boolean(message)} message={message?.text || ''} type={message?.type || 'info'} onClose={closeToast} duration={message?.type === 'error' ? 5000 : 3500} />

      <div className="erp-heading">
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: '#0f172a', margin: 0 }}>Stock Adjustment</h1>
          <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: 14 }}>
            Correct physical balances with a traceable reason. Every adjustment is journaled automatically.
          </p>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 9, padding: 4, background: '#f1f5f9', borderRadius: 12, width: 'max-content' }}>
        {(Object.entries(tabMeta) as [Tab, typeof tabMeta[Tab]][]).map(([key, meta]) => (
          <button key={key} onClick={() => setTab(key)}
            style={{
              display: 'flex', alignItems: 'center', gap: 7, padding: '9px 16px', borderRadius: 9,
              border: 'none', fontSize: 13, fontWeight: 700, cursor: 'pointer',
              background: tab === key ? '#fff' : 'transparent',
              color: tab === key ? meta.color : '#64748b',
              boxShadow: tab === key ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
              transition: 'all 0.15s',
            }}>
            {meta.icon}
            {meta.label}
          </button>
        ))}
      </div>

      {tab === 'adjust' && (
        <div className="erp-card" style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{
            padding: '14px 18px', background: 'linear-gradient(135deg, #fef3c7, #fde68a)',
            borderBottom: '1px solid #fcd34d',
          }}>
            <h2 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: '#92400e', display: 'flex', alignItems: 'center', gap: 8 }}>
              <Package size={16} /> Stock Records
              <span style={{ fontWeight: 400, fontSize: 12, color: '#a16207' }}>— select a row to adjust</span>
            </h2>
          </div>
          <div className="erp-table-wrap">
            <table>
              <thead>
                <tr>
                  {showLocationColumn && <th style={{ padding: '8px 10px', fontSize: 11 }}>Location</th>}
                  <th style={{ padding: '8px 10px', fontSize: 11 }}>Product</th>
                  <th style={{ padding: '8px 10px', fontSize: 11 }}>Variant</th>
                  <th style={{ padding: '8px 10px', fontSize: 11 }}>Physical</th>
                  <th style={{ padding: '8px 10px', fontSize: 11 }}>Available</th>
                  <th style={{ padding: '8px 10px', fontSize: 11 }}>Cost</th>
                  <th style={{ padding: '8px 10px', fontSize: 11, textAlign: 'center' }}></th>
                </tr>
              </thead>
              <tbody>
                {inventoryStock.length === 0 && (
                  <tr><td colSpan={showLocationColumn ? 7 : 6} className="erp-empty">No stock records found.</td></tr>
                )}
                {inventoryStock.map((x: any) => (
                  <tr key={`${x.location}-${x.product_variant}`}
                    style={{ cursor: 'pointer', transition: 'background 0.1s' }}
                    onMouseEnter={e => { e.currentTarget.style.background = '#f8fafc'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = ''; }}
                    onClick={() => openAdjustment(x)}>
                    {showLocationColumn && <td style={{ padding: '8px 10px', fontSize: 12 }}>{x.location_name}</td>}
                    <td style={{ padding: '8px 10px', fontSize: 12, fontWeight: 600 }}>{x.product_name}</td>
                    <td style={{ padding: '8px 10px', fontSize: 12 }}>{`${x.variant_color || ''} ${x.variant_size || ''}`.trim() || x.variant_sku || '—'}</td>
                    <td style={{ padding: '8px 10px', fontSize: 12 }}>{x.quantity}</td>
                    <td style={{ padding: '8px 10px', fontSize: 12 }}>{x.available_quantity}</td>
                    <td style={{ padding: '8px 10px', fontSize: 12 }}>{money(x.average_cost)}</td>
                    <td style={{ padding: '8px 10px', textAlign: 'center' }}>
                      <span style={{
                        display: 'inline-flex', padding: '3px 10px', borderRadius: 6,
                        fontSize: 11, fontWeight: 700, background: '#fef3c7', color: '#b45309',
                      }}>
                        Adjust
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'history' && (
        <div className="erp-card" style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{
            padding: '14px 18px', background: 'linear-gradient(135deg, #ecfeff, #cffafe)',
            borderBottom: '1px solid #67e8f9',
          }}>
            <h2 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: '#0e7490', display: 'flex', alignItems: 'center', gap: 8 }}>
              <History size={16} /> Stock Adjustment History
            </h2>
            <p style={{ margin: '4px 0 0', fontSize: 12, color: '#0891b2' }}>
              Every adjustment with its reason, valuation, and posted journal.
            </p>
          </div>
          <div className="erp-table-wrap">
            <table>
              <thead>
                <tr>
                  <th style={{ padding: '8px 10px', fontSize: 11 }}>Date</th>
                  {showLocationColumn && <th style={{ padding: '8px 10px', fontSize: 11 }}>Location</th>}
                  <th style={{ padding: '8px 10px', fontSize: 11 }}>Variant</th>
                  <th style={{ padding: '8px 10px', fontSize: 11 }}>Reason</th>
                  <th style={{ padding: '8px 10px', fontSize: 11 }}>Type</th>
                  <th style={{ padding: '8px 10px', fontSize: 11 }}>Δ</th>
                  <th style={{ padding: '8px 10px', fontSize: 11, textAlign: 'right' }}>Value</th>
                  <th style={{ padding: '8px 10px', fontSize: 11 }}>Notes</th>
                  <th style={{ padding: '8px 10px', fontSize: 11 }}>Journal</th>
                </tr>
              </thead>
              <tbody>
                {adjustments.length === 0 && (
                  <tr><td colSpan={showLocationColumn ? 9 : 8} className="erp-empty">No adjustments yet.</td></tr>
                )}
                {adjustments.map((m: any) => (
                  <tr key={m.id}>
                    <td style={{ padding: '8px 10px', fontSize: 12, whiteSpace: 'nowrap' }}>{date(m.created_at)}</td>
                    {showLocationColumn && <td style={{ padding: '8px 10px', fontSize: 12 }}>{m.location_name}</td>}
                    <td style={{ padding: '8px 10px', fontSize: 12 }}>{variantDisplay({ product_name: m.product_name, color: m.variant_color, size: m.variant_size, sku: m.variant_sku })}</td>
                    <td style={{ padding: '8px 10px', fontSize: 12 }}>{m.reason_name}</td>
                    <td style={{ padding: '8px 10px', fontSize: 12 }}>
                      <span style={{
                        display: 'inline-flex', padding: '2px 8px', borderRadius: 999, fontSize: 10, fontWeight: 700,
                        background: m.adjustment_type === 'LOSS' ? '#fee2e2' : '#dcfce7',
                        color: m.adjustment_type === 'LOSS' ? '#b91c1c' : '#15803d',
                      }}>
                        {m.adjustment_type === 'LOSS' ? 'Loss' : 'Gain'}
                      </span>
                    </td>
                    <td style={{
                      padding: '8px 10px', fontSize: 12, fontWeight: 700,
                      color: (m.quantity_delta || 0) > 0 ? '#16a34a' : '#dc2626',
                    }}>
                      {(m.quantity_delta > 0 ? '+' : '')}{m.quantity_delta}
                    </td>
                    <td style={{ padding: '8px 10px', fontSize: 12, textAlign: 'right', fontWeight: 700 }}>{money(m.total_value)}</td>
                    <td style={{ padding: '8px 10px', fontSize: 12, maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={m.notes}>
                      {m.notes || '—'}
                    </td>
                    <td style={{ padding: '8px 10px', fontSize: 12 }}>{m.journal_entry ? `#${m.journal_entry}` : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {adjustingStock && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(15,23,42,0.55)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
          backdropFilter: 'blur(2px)',
        }} onClick={closeAdjustment}>
          <div style={{
            width: '100%', maxWidth: 680, maxHeight: '94vh', overflowY: 'auto',
            background: '#fff', borderRadius: 18, boxShadow: '0 25px 60px rgba(0,0,0,0.25)',
          }} onClick={e => e.stopPropagation()}>
            <div style={{
              position: 'sticky', top: 0, zIndex: 2,
              display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
              padding: '16px 20px', background: 'linear-gradient(135deg, #fef3c7, #fde68a)',
              borderBottom: '1px solid #fcd34d', borderTopLeftRadius: 18, borderTopRightRadius: 18,
            }}>
              <div>
                <h2 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#92400e', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <AlertTriangle size={17} /> Adjust Stock
                </h2>
                <div style={{ marginTop: 4, fontSize: 12.5, color: '#a16207', fontWeight: 600 }}>
                  {adjustingStock.product_name}
                  {`${adjustingStock.variant_color || ''} ${adjustingStock.variant_size || ''}`.trim() && <span style={{ fontWeight: 400, color: '#a16207' }}> — {`${adjustingStock.variant_color || ''} ${adjustingStock.variant_size || ''}`.trim()}</span>}
                  {showLocationColumn && adjustingStock.location_name && <span style={{ fontWeight: 400, color: '#a16207' }}> · {adjustingStock.location_name}</span>}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
                <button type="button" onClick={saveAdjustment} disabled={!canSave}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 6, border: 'none', cursor: (saving || !canSave) ? 'not-allowed' : 'pointer',
                    background: saving ? '#fcd34d' : '#f59e0b', color: '#fff', padding: '8px 14px', borderRadius: 9,
                    fontSize: 12.5, fontWeight: 700, boxShadow: '0 2px 6px rgba(245,158,11,0.35)',
                    opacity: (saving || !canSave) ? 0.6 : 1,
                  }}>
                  {saving ? <>Adjusting…</> : <><AlertTriangle size={15} /> Save Adjustment</>}
                </button>
                <button onClick={closeAdjustment} style={{
                  border: 0, background: 'rgba(255,255,255,0.7)', borderRadius: 8,
                  width: 34, height: 34, display: 'grid', placeItems: 'center',
                  cursor: 'pointer', color: '#92400e',
                }}><X size={16} /></button>
              </div>
            </div>

            <div style={{ padding: 18, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div>
                <div style={{
                  display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 16,
                }}>
                  {[
                    { label: 'Current Physical', value: currentQty, color: '#334155' },
                    { label: 'Avg Cost', value: money(adjustingStock.average_cost), color: '#334155' },
                    { label: 'Available', value: adjustingStock.available_quantity, color: '#334155' },
                  ].map(s => (
                    <div key={s.label} style={{ background: '#f8fafc', borderRadius: 10, padding: '10px 12px' }}>
                      <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: '#94a3b8', marginBottom: 4 }}>{s.label}</div>
                      <div style={{ fontSize: 16, fontWeight: 700, color: s.color }}>{s.value}</div>
                    </div>
                  ))}
                </div>

                <Form onSubmit={saveAdjustment}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                    <div>
                      <label style={fieldLabel}>New physical quantity</label>
                      <input
                        style={{ ...inputStyle, borderColor: '#f59e0b', background: '#fffbeb', fontSize: 15, fontWeight: 700 }}
                        type="number" value={value('quantity')} placeholder="0"
                        onChange={e => setForm({ ...form, quantity: e.target.value })} />
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6 }}>
                        {delta !== 0 && (
                          <span style={{
                            display: 'inline-flex', alignItems: 'center', padding: '2px 8px', borderRadius: 999,
                            fontSize: 11.5, fontWeight: 700,
                            background: delta > 0 ? '#dcfce7' : '#fee2e2',
                            color: delta > 0 ? '#15803d' : '#b91c1c',
                          }}>
                            {delta > 0 ? '+' : ''}{delta} units {delta > 0 ? 'added' : 'removed'}
                          </span>
                        )}
                        <span style={{ fontSize: 11, color: '#94a3b8' }}>
                          Set to the actual physical count.
                        </span>
                      </div>
                    </div>

                    <div>
                      <label style={fieldLabel}>Adjustment reason</label>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {reasons.length === 0 && <span style={{ fontSize: 12, color: '#94a3b8' }}>No active reasons configured.</span>}
                        {reasons.map(r => (
                          <label key={r.id} title={r.type === 'LOSS' ? 'Stock reduction' : r.type === 'GAIN' ? 'Stock increase' : 'Direction depends on count change'}
                            style={{
                              display: 'flex', alignItems: 'center', gap: 6, padding: '7px 11px',
                              borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                              background: Number(value('adjustment_reason')) === r.id ? '#fef3c7' : '#f8fafc',
                              border: Number(value('adjustment_reason')) === r.id ? '1.5px solid #f59e0b' : '1px solid #e2e8f0',
                              color: Number(value('adjustment_reason')) === r.id ? '#92400e' : '#475569',
                              transition: 'all 0.12s',
                            }}>
                            <input type="radio" name="adj_reason" value={r.id}
                              checked={Number(value('adjustment_reason')) === r.id}
                              onChange={e => setForm({ ...form, adjustment_reason: e.target.value })}
                              style={{ width: 14, height: 14, margin: 0, accentColor: '#f59e0b' }} />
                            <span>{r.name}</span>
                          </label>
                        ))}
                      </div>
                      {selectedReason && selectedReason.requires_note && (
                        <div style={{ fontSize: 11, color: '#a16207', marginTop: 6 }}>
                          This reason requires a note below.
                        </div>
                      )}
                    </div>

                    {isGain ? (
                      <div>
                        <label style={fieldLabel}>Unit cost (৳) <span style={{ color: '#dc2626' }}>*</span></label>
                        <input style={{ ...inputStyle, borderColor: '#16a34a', background: '#f0fdf4' }} type="number"
                          value={value('unit_cost')} placeholder="Estimated acquisition cost" step="1"
                          onChange={e => setForm({ ...form, unit_cost: e.target.value })} />
                        <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>
                          Stock increases are valued at the estimated cost per unit because the acquisition cost is unknown.
                        </div>
                      </div>
                    ) : (
                      <div style={{ background: '#f8fafc', borderRadius: 10, padding: '10px 12px', fontSize: 12, color: '#64748b' }}>
                        Stock reductions are valued automatically at the average cost: <strong style={{ color: '#334155' }}>{money(adjustingStock.average_cost)}/unit</strong>. No manual cost entry.
                      </div>
                    )}

                    <div><label style={fieldLabel}>Notes {selectedReason?.requires_note ? <span style={{ color: '#dc2626' }}>*</span> : <span style={{ fontWeight: 400, textTransform: 'none', color: '#94a3b8' }}>optional</span>}</label>
                      <textarea value={value('notes')}
                        placeholder="Damage report #, approval reference, etc."
                        rows={2}
                        onChange={e => setForm({ ...form, notes: e.target.value })}
                        style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }} />
                    </div>
                  </div>
                </Form>
              </div>

              {delta !== 0 && selectedReason && (
                <div>
                  <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: '#94a3b8', marginBottom: 8 }}>
                    Adjustment Summary
                  </div>
                  <div style={{ background: '#f8fafc', borderRadius: 12, padding: 14, marginBottom: 14, fontSize: 12.5 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0' }}><span style={{ color: '#64748b' }}>Product</span><strong>{adjustingStock.product_name} {`${adjustingStock.variant_color || ''} ${adjustingStock.variant_size || ''}`.trim()}</strong></div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0' }}><span style={{ color: '#64748b' }}>Change</span><strong style={{ color: delta > 0 ? '#15803d' : '#b91c1c' }}>{delta > 0 ? '+' : ''}{delta} units</strong></div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0' }}><span style={{ color: '#64748b' }}>Reason</span><strong>{selectedReason.name}</strong></div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0' }}><span style={{ color: '#64748b' }}>Unit value</span><strong>{money(isGain ? value('unit_cost') : adjustingStock.average_cost)} / unit</strong></div>
                    <div style={{ borderTop: '1px solid #e2e8f0', marginTop: 6, paddingTop: 6, display: 'flex', justifyContent: 'space-between', fontWeight: 700 }}>
                      <span>Estimated Value</span><span>{money(totalValue)}</span>
                    </div>
                  </div>

                  <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: '#94a3b8', marginBottom: 8 }}>
                    Accounting Impact
                  </div>
                  <div style={{ background: '#fffbeb', borderRadius: 12, border: '1px solid #fcd34d', padding: 14, fontSize: 12.5 }}>
                    {direction === 'LOSS' && (
                      <>
                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0' }}><span style={{ color: '#64748b' }}>Dr</span><strong>{expenseAcct || '5300'} {expenseName || 'Inventory Variance Expense'} — {money(totalValue)}</strong></div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0' }}><span style={{ color: '#64748b' }}>Cr</span><strong>{assetAcct || '1200'} {assetName || 'Inventory Asset'} — {money(totalValue)}</strong></div>
                      </>
                    )}
                    {direction === 'GAIN' && (
                      <>
                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0' }}><span style={{ color: '#64748b' }}>Dr</span><strong>{assetAcct || '1200'} {assetName || 'Inventory Asset'} — {money(totalValue)}</strong></div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0' }}><span style={{ color: '#64748b' }}>Cr</span><strong>{gainAcct || '4200'} {gainName || 'Inventory Variance Gain'} — {money(totalValue)}</strong></div>
                      </>
                    )}
                    <div style={{ marginTop: 6, fontSize: 11, color: '#a16207' }}>
                      No cash/bank account is involved — this is an internal inventory correction.
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InventoryStockAdjustment;